import {
  RoadBlockageStatus,
  RoadSegmentDirection,
} from '@backoffice-frontend/graphql'
import * as maplibregl from 'maplibre-gl'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useRouteBuilderCustomRoadBlockagesLazyQuery } from './RoadBlockages.hook'
import {
  extractCenter,
  extractEndpoints,
  formatReason,
  resolveRegionId,
} from './roadBlockagesUtils'

const ACTIVE_SOURCE_ID = 'road-blockages-active-source'
const ACTIVE_LAYER_ID = 'road-blockages-active-line'
const ACTIVE_ARROWS_LAYER_ID = 'road-blockages-active-arrows'
const ACTIVE_CENTER_SOURCE_ID = 'road-blockages-active-center-source'
const ACTIVE_CENTER_ICON_LAYER_ID = 'road-blockages-active-center-icon'
const ACTIVE_ENDPOINTS_SOURCE_ID = 'road-blockages-active-endpoints-source'
const ACTIVE_ENDPOINTS_LAYER_ID = 'road-blockages-active-endpoints'
const SCHEDULED_SOURCE_ID = 'road-blockages-scheduled-source'
const SCHEDULED_LAYER_ID = 'road-blockages-scheduled-line'
const SCHEDULED_ARROWS_LAYER_ID = 'road-blockages-scheduled-arrows'
const SCHEDULED_CENTER_SOURCE_ID = 'road-blockages-scheduled-center-source'
const SCHEDULED_CENTER_ICON_LAYER_ID = 'road-blockages-scheduled-center-icon'
const SCHEDULED_ENDPOINTS_SOURCE_ID =
  'road-blockages-scheduled-endpoints-source'
const SCHEDULED_ENDPOINTS_LAYER_ID = 'road-blockages-scheduled-endpoints'

const ARROW_IMAGE_ID = 'road-blockage-arrow'
const BARRIER_ACTIVE_IMAGE_ID = 'road-blockage-barrier-active'
const BARRIER_SCHEDULED_IMAGE_ID = 'road-blockage-barrier-scheduled'

// White chevron arrow from the designs, served from the shared public assets
// dir and loaded as a sprite.
const ARROW_IMAGE_PATH = '/assets/route-builder/blockageArrow.png'

const ACTIVE_COLOR = '#D32F2F'
const SCHEDULED_COLOR = '#757575'

// Blockages are lane-level detail, so they only render once the user has zoomed
// in past this level.
const BLOCKAGE_MIN_ZOOM = 15

const LINE_WIDTH = 10
// The chevron sprite is 32px wide; once rotated along the line that width
// becomes the cross-line extent, so icon-size = LINE_WIDTH / 32 makes the arrow
// exactly as wide as the lane.
const ARROW_IMAGE_NATIVE_WIDTH = 32
const ARROW_ICON_SIZE = LINE_WIDTH / ARROW_IMAGE_NATIVE_WIDTH
// Ground spacing (px at zoom 16) between arrows along a line. Lower = more
// frequent arrows. The interpolation below doubles this per zoom level so the
// arrow count stays constant relative to the line's ground length.
const ARROW_SPACING_BASE = 24
const ARROW_SYMBOL_SPACING: maplibregl.ExpressionSpecification = [
  'interpolate',
  ['exponential', 2],
  ['zoom'],
  11,
  ARROW_SPACING_BASE / 32,
  21,
  ARROW_SPACING_BASE * 32,
]
// Rotate the chevron with the line so it points along the lane's travel
// direction. The sprite points up, so a 90° base rotation aligns it with the
// line's forward (digitized) direction; backward segments use 270°.
const ARROW_ICON_ROTATE: maplibregl.ExpressionSpecification = [
  'case',
  ['==', ['get', 'direction'], RoadSegmentDirection.Backward],
  270,
  90,
]

/** Layout for a direction-arrow symbol layer. */
function arrowLayout(): maplibregl.SymbolLayerSpecification['layout'] {
  return {
    'symbol-placement': 'line',
    'symbol-spacing': ARROW_SYMBOL_SPACING,
    'icon-image': ARROW_IMAGE_ID,
    'icon-size': ARROW_ICON_SIZE,
    'icon-overlap': 'always',
    'icon-rotate': ARROW_ICON_ROTATE,
  }
}
// The barrier icon sits in a circle roughly twice the line width.
const BARRIER_CANVAS_SIZE = 48
const BARRIER_ICON_SIZE = (LINE_WIDTH * 2.4) / BARRIER_CANVAS_SIZE

/**
 * Create a barrier/roadblock icon inside a colored circle. Drawn on a
 * high-resolution canvas (the coordinate system is scaled to a 24px design box)
 * so it stays crisp when rendered larger than the line.
 */
function createBarrierImage(color: string): ImageData {
  const size = BARRIER_CANVAS_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  ctx.scale(size / 24, size / 24)

  // Red/gray circle background
  ctx.beginPath()
  ctx.arc(12, 12, 11, 0, Math.PI * 2)
  ctx.fillStyle = color
  ctx.fill()

  // White barrier icon (simplified construction barrier), vertically centered
  // on the circle center (12,12) so the glyph sits in the middle of the circle.
  ctx.strokeStyle = '#FFFFFF'
  ctx.lineCap = 'round'
  ctx.lineWidth = 2
  // Horizontal bar
  ctx.beginPath()
  ctx.moveTo(6, 12)
  ctx.lineTo(18, 12)
  ctx.stroke()
  // Left leg
  ctx.beginPath()
  ctx.moveTo(8, 12)
  ctx.lineTo(7, 16)
  ctx.stroke()
  // Right leg
  ctx.beginPath()
  ctx.moveTo(16, 12)
  ctx.lineTo(17, 16)
  ctx.stroke()
  // Top diagonal stripes
  ctx.lineWidth = 1.6
  ctx.beginPath()
  ctx.moveTo(8, 8.5)
  ctx.lineTo(10.5, 12)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(11.5, 8.5)
  ctx.lineTo(14, 12)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(15, 8.5)
  ctx.lineTo(16.5, 10.6)
  ctx.stroke()
  // Top bar
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(7, 8.5)
  ctx.lineTo(17, 8.5)
  ctx.stroke()

  return ctx.getImageData(0, 0, size, size)
}

/** Load and register blockage images into the map (idempotent) */
async function ensureBlockageImages(map: maplibregl.Map) {
  if (!map.hasImage(ARROW_IMAGE_ID)) {
    const { data } = await map.loadImage(ARROW_IMAGE_PATH)
    // Re-check after the await in case another caller registered it meanwhile.
    if (data && !map.hasImage(ARROW_IMAGE_ID)) {
      map.addImage(ARROW_IMAGE_ID, data, { sdf: false })
    }
  }
  if (!map.hasImage(BARRIER_ACTIVE_IMAGE_ID)) {
    map.addImage(BARRIER_ACTIVE_IMAGE_ID, createBarrierImage(ACTIVE_COLOR), {
      sdf: false,
    })
  }
  if (!map.hasImage(BARRIER_SCHEDULED_IMAGE_ID)) {
    map.addImage(
      BARRIER_SCHEDULED_IMAGE_ID,
      createBarrierImage(SCHEDULED_COLOR),
      { sdf: false },
    )
  }
}

const ALL_LAYERS = [
  ACTIVE_LAYER_ID,
  ACTIVE_ARROWS_LAYER_ID,
  ACTIVE_CENTER_ICON_LAYER_ID,
  ACTIVE_ENDPOINTS_LAYER_ID,
  SCHEDULED_LAYER_ID,
  SCHEDULED_ARROWS_LAYER_ID,
  SCHEDULED_CENTER_ICON_LAYER_ID,
  SCHEDULED_ENDPOINTS_LAYER_ID,
]
const ALL_SOURCES = [
  ACTIVE_SOURCE_ID,
  ACTIVE_CENTER_SOURCE_ID,
  ACTIVE_ENDPOINTS_SOURCE_ID,
  SCHEDULED_SOURCE_ID,
  SCHEDULED_CENTER_SOURCE_ID,
  SCHEDULED_ENDPOINTS_SOURCE_ID,
]

function removeLayers(map: maplibregl.Map) {
  for (const id of ALL_LAYERS) {
    if (map.getLayer(id)) map.removeLayer(id)
  }
  for (const id of ALL_SOURCES) {
    if (map.getSource(id)) map.removeSource(id)
  }
}

const POPUP_CLASS = 'road-blockage-popup'
const POPUP_STYLE_ID = 'road-blockage-popup-styles'
const POPUP_BG = '#1a1a1a'

/**
 * Inject the dark-tooltip styling for the blockage hover popup once. MapLibre's
 * default popup is a white box with a white tip; we recolor both the content
 * box and the directional tip (per anchor) to a dark rounded tooltip.
 */
function ensurePopupStyles() {
  if (document.getElementById(POPUP_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = POPUP_STYLE_ID
  style.textContent = `
    .${POPUP_CLASS} .maplibregl-popup-content {
      background: ${POPUP_BG};
      color: #fff;
      border-radius: 12px;
      padding: 14px 18px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    }
    .${POPUP_CLASS}.maplibregl-popup-anchor-top .maplibregl-popup-tip { border-bottom-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-bottom .maplibregl-popup-tip { border-top-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-left .maplibregl-popup-tip { border-right-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-right .maplibregl-popup-tip { border-left-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-top-left .maplibregl-popup-tip { border-bottom-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-top-right .maplibregl-popup-tip { border-bottom-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-bottom-left .maplibregl-popup-tip { border-top-color: ${POPUP_BG}; }
    .${POPUP_CLASS}.maplibregl-popup-anchor-bottom-right .maplibregl-popup-tip { border-top-color: ${POPUP_BG}; }
  `
  document.head.appendChild(style)
}

export function useRoadBlockagesOverlay(
  map: maplibregl.Map | null,
  metropolisName: string | undefined,
  insertBeforeLayer?: string,
) {
  const [visible, setVisible] = useState(false)
  const [imagesReady, setImagesReady] = useState(false)
  const [fetchBlockages, { data, loading }] =
    useRouteBuilderCustomRoadBlockagesLazyQuery()
  const addedRef = useRef(false)

  const regionId = resolveRegionId(metropolisName)

  // Register sprites (arrow PNG + canvas barrier icons) so layers never
  // reference a missing image. The arrow loads asynchronously.
  useEffect(() => {
    if (!map) return
    void ensureBlockageImages(map).then(() => setImagesReady(true))
  }, [map])

  const toggle = useCallback((checked?: boolean) => {
    setVisible(prev => checked ?? !prev)
  }, [])

  // Clear stale layers when switching metros while overlay is visible
  const prevRegionIdRef = useRef(regionId)
  useEffect(() => {
    if (prevRegionIdRef.current !== regionId && map && addedRef.current) {
      removeLayers(map)
      addedRef.current = false
    }
    prevRegionIdRef.current = regionId
  }, [regionId, map])

  // Fetch data when toggled on and regionId is available
  useEffect(() => {
    if (visible && regionId) {
      // Clear stale layers before fetching new data so the user doesn't see
      // blockages from the previous region during loading
      if (map && addedRef.current) {
        removeLayers(map)
        addedRef.current = false
      }
      fetchBlockages({
        variables: {
          regionId,
          filter: {
            statuses: [RoadBlockageStatus.Active, RoadBlockageStatus.Scheduled],
          },
        },
      })
    }
  }, [visible, regionId, fetchBlockages, map])

  // Add/remove layers based on visibility and data
  useEffect(() => {
    if (!map) return

    if (!visible || !data?.customRoadBlockages) {
      if (addedRef.current) {
        removeLayers(map)
        addedRef.current = false
      }
      return
    }

    // Wait until sprites are loaded; this effect re-runs when they are ready.
    if (!imagesReady) return

    const blockages = data.customRoadBlockages

    const activeFeatures: GeoJSON.Feature[] = []
    const scheduledFeatures: GeoJSON.Feature[] = []
    const activeCenters: GeoJSON.Feature<GeoJSON.Point>[] = []
    const scheduledCenters: GeoJSON.Feature<GeoJSON.Point>[] = []
    const activeEndpoints: GeoJSON.Feature<GeoJSON.Point>[] = []
    const scheduledEndpoints: GeoJSON.Feature<GeoJSON.Point>[] = []

    for (const blockage of blockages) {
      const isActive = blockage.status === RoadBlockageStatus.Active
      const targetFeatures = isActive ? activeFeatures : scheduledFeatures
      const targetCenters = isActive ? activeCenters : scheduledCenters
      const targetEndpoints = isActive ? activeEndpoints : scheduledEndpoints

      for (const segment of blockage.segments) {
        try {
          const geometry = JSON.parse(segment.geometry) as GeoJSON.Geometry
          targetFeatures.push({
            type: 'Feature',
            properties: {
              name: blockage.name,
              status: blockage.status,
              reason: blockage.reason,
              validFrom: blockage.validFrom,
              validTo: blockage.validTo,
              direction: segment.direction,
            },
            geometry,
          })
          const center = extractCenter(geometry)
          if (center) targetCenters.push(center)
          targetEndpoints.push(...extractEndpoints(geometry))
        } catch {
          // Skip segments with invalid geometry
        }
      }
    }

    const activeGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: activeFeatures,
    }
    const scheduledGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: scheduledFeatures,
    }
    const activeCentersGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: activeCenters,
    }
    const scheduledCentersGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: scheduledCenters,
    }
    const activeEndpointsGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: activeEndpoints,
    }
    const scheduledEndpointsGeoJson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: scheduledEndpoints,
    }

    // Remove existing layers before re-adding
    if (addedRef.current) {
      removeLayers(map)
    }

    const beforeLayer =
      insertBeforeLayer && map.getLayer(insertBeforeLayer)
        ? insertBeforeLayer
        : undefined

    // --- Active blockages ---
    map.addSource(ACTIVE_SOURCE_ID, { type: 'geojson', data: activeGeoJson })
    map.addSource(ACTIVE_CENTER_SOURCE_ID, {
      type: 'geojson',
      data: activeCentersGeoJson,
    })
    map.addSource(ACTIVE_ENDPOINTS_SOURCE_ID, {
      type: 'geojson',
      data: activeEndpointsGeoJson,
    })

    // Base line
    map.addLayer(
      {
        id: ACTIVE_LAYER_ID,
        type: 'line',
        source: ACTIVE_SOURCE_ID,
        minzoom: BLOCKAGE_MIN_ZOOM,
        paint: {
          'line-color': ACTIVE_COLOR,
          'line-width': LINE_WIDTH,
          'line-opacity': 0.9,
        },
        layout: { 'line-join': 'round', 'line-cap': 'round' },
      },
      beforeLayer,
    )

    // Direction arrows along lines (only for one-way segments)
    map.addLayer({
      id: ACTIVE_ARROWS_LAYER_ID,
      type: 'symbol',
      source: ACTIVE_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      filter: ['!=', ['get', 'direction'], RoadSegmentDirection.Both],
      layout: arrowLayout(),
    })

    // Center barrier icon — placed at each segment's midpoint and kept upright
    map.addLayer({
      id: ACTIVE_CENTER_ICON_LAYER_ID,
      type: 'symbol',
      source: ACTIVE_CENTER_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      layout: {
        'icon-image': BARRIER_ACTIVE_IMAGE_ID,
        'icon-size': BARRIER_ICON_SIZE,
        'icon-overlap': 'always',
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
      },
    })

    // Endpoint circles
    map.addLayer({
      id: ACTIVE_ENDPOINTS_LAYER_ID,
      type: 'circle',
      source: ACTIVE_ENDPOINTS_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      paint: {
        'circle-radius': 5,
        'circle-color': '#FFFFFF',
        'circle-stroke-width': 4,
        'circle-stroke-color': ACTIVE_COLOR,
      },
    })

    // --- Scheduled blockages ---
    map.addSource(SCHEDULED_SOURCE_ID, {
      type: 'geojson',
      data: scheduledGeoJson,
    })
    map.addSource(SCHEDULED_CENTER_SOURCE_ID, {
      type: 'geojson',
      data: scheduledCentersGeoJson,
    })
    map.addSource(SCHEDULED_ENDPOINTS_SOURCE_ID, {
      type: 'geojson',
      data: scheduledEndpointsGeoJson,
    })

    // Base line
    map.addLayer(
      {
        id: SCHEDULED_LAYER_ID,
        type: 'line',
        source: SCHEDULED_SOURCE_ID,
        minzoom: BLOCKAGE_MIN_ZOOM,
        paint: {
          'line-color': SCHEDULED_COLOR,
          'line-width': LINE_WIDTH,
          'line-opacity': 0.9,
        },
        layout: { 'line-join': 'round', 'line-cap': 'round' },
      },
      beforeLayer,
    )

    // Direction arrows along lines (only for one-way segments)
    map.addLayer({
      id: SCHEDULED_ARROWS_LAYER_ID,
      type: 'symbol',
      source: SCHEDULED_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      filter: ['!=', ['get', 'direction'], RoadSegmentDirection.Both],
      layout: arrowLayout(),
    })

    // Center barrier icon — placed at each segment's midpoint and kept upright
    map.addLayer({
      id: SCHEDULED_CENTER_ICON_LAYER_ID,
      type: 'symbol',
      source: SCHEDULED_CENTER_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      layout: {
        'icon-image': BARRIER_SCHEDULED_IMAGE_ID,
        'icon-size': BARRIER_ICON_SIZE,
        'icon-overlap': 'always',
        'icon-rotation-alignment': 'viewport',
        'icon-pitch-alignment': 'viewport',
      },
    })

    // Endpoint circles
    map.addLayer({
      id: SCHEDULED_ENDPOINTS_LAYER_ID,
      type: 'circle',
      source: SCHEDULED_ENDPOINTS_SOURCE_ID,
      minzoom: BLOCKAGE_MIN_ZOOM,
      paint: {
        'circle-radius': 5,
        'circle-color': '#FFFFFF',
        'circle-stroke-width': 4,
        'circle-stroke-color': SCHEDULED_COLOR,
      },
    })

    addedRef.current = true
  }, [map, visible, data, imagesReady, insertBeforeLayer])

  // Hover popup for blockage details — re-registers handlers when layers are
  // recreated (the data dependency triggers that recreation).
  const popupRef = useRef<maplibregl.Popup | null>(null)
  useEffect(() => {
    if (!map || !visible || !addedRef.current) return

    ensurePopupStyles()
    const popup = new maplibregl.Popup({
      offset: 15,
      closeButton: false,
      closeOnClick: false,
      maxWidth: 'none',
      className: POPUP_CLASS,
    })
    popupRef.current = popup

    const formatDate = (iso: string | null) => {
      if (!iso) return '—'
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    }

    const handleMouseEnter = (
      e: maplibregl.MapMouseEvent & {
        features?: maplibregl.MapGeoJSONFeature[]
      },
    ) => {
      map.getCanvas().style.cursor = 'pointer'
      const feature = e.features?.[0]
      if (!feature) return

      const { reason, validFrom, validTo } = feature.properties

      popup
        .setLngLat(e.lngLat)
        .setHTML(
          `<div style="font-size:15px;line-height:1.6;white-space:nowrap">
            <div>Blockage type: <b>${reason ? formatReason(reason) : '—'}</b></div>
            <div>Timeframe: <b>${formatDate(validFrom)} - ${formatDate(validTo)}</b></div>
          </div>`,
        )
        .addTo(map)
    }

    const handleMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (popup.isOpen()) {
        popup.setLngLat(e.lngLat)
      }
    }

    const handleMouseLeave = () => {
      map.getCanvas().style.cursor = ''
      popup.remove()
    }

    const interactiveLayers = [ACTIVE_LAYER_ID, SCHEDULED_LAYER_ID]
    for (const layerId of interactiveLayers) {
      map.on('mouseenter', layerId, handleMouseEnter)
      map.on('mousemove', layerId, handleMouseMove)
      map.on('mouseleave', layerId, handleMouseLeave)
    }

    return () => {
      for (const layerId of interactiveLayers) {
        map.off('mouseenter', layerId, handleMouseEnter)
        map.off('mousemove', layerId, handleMouseMove)
        map.off('mouseleave', layerId, handleMouseLeave)
      }
      popup.remove()
    }
  }, [map, visible, data])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (!map || !addedRef.current) return
      removeLayers(map)
      popupRef.current?.remove()
    }
  }, [map])

  return { visible, loading, toggle }
}
